import { useState } from 'react';
import { Modal, ModalContent, Button, Input, Control, Label, TextArea, LockIcon } from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { sftpIpBlocksApi } from '@/api/sftpIpBlocks';
import type { SftpIpBlock, CreateSftpIpBlockPayload } from '@/api/sftpIpBlocks';

interface BlockIpDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

// Accepts a bare IPv4 address; blocks target a single source IP.
const looksLikeIp = (value: string): boolean => /^(\d{1,3}\.){3}\d{1,3}$/.test(value.trim());

const BlockIpDialog = ({ onClose, onSuccess }: BlockIpDialogProps) => {
  const [ipAddress, setIpAddress] = useState('');
  const [reason, setReason] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');

  const { mutate: block, isSubmitting } = useApiMutation<CreateSftpIpBlockPayload, SftpIpBlock>({
    mutationFn: (data) => sftpIpBlocksApi.create(data),
    successMessage: 'IP address blocked',
    errorMessage: 'Failed to block IP address',
    onSuccess: () => onSuccess(),
  });

  const ipValid = looksLikeIp(ipAddress);
  const durationTrimmed = durationMinutes.trim();
  const durationNumber = Number(durationTrimmed);
  const durationValid =
    durationTrimmed === '' || (Number.isInteger(durationNumber) && durationNumber > 0);
  const isValid = ipValid && durationValid;

  const handleSubmit = async () => {
    if (!isValid) return;
    await block({
      ipAddress: ipAddress.trim(),
      reason: reason.trim() || undefined,
      durationMinutes: durationTrimmed === '' ? undefined : durationNumber,
    });
  };

  return (
    <Modal
      title="Block IP Address"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<LockIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Blocking...' : 'Block IP'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-color-secondary">
            Manually block a source IP from connecting to the SFTP gateway.
          </p>

          <Control hideSpacing="bottom">
            <Label htmlFor="block-ip">IP Address</Label>
            <Input
              id="block-ip"
              placeholder="e.g., 203.0.113.5"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              required
            />
            {ipAddress.trim() && !ipValid && (
              <p className="mt-1 text-xs text-color-error">
                Enter a valid IPv4 address (e.g. 203.0.113.5).
              </p>
            )}
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="block-reason">Reason (Optional)</Label>
            <TextArea
              id="block-reason"
              rows={2}
              placeholder="e.g., Repeated brute-force attempts"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="block-duration">Duration (minutes, optional)</Label>
            <Input
              id="block-duration"
              type="number"
              placeholder="e.g., 60"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
            />
            {durationTrimmed !== '' && !durationValid ? (
              <p className="mt-1 text-xs text-color-error">
                Enter a positive whole number of minutes.
              </p>
            ) : (
              <p className="mt-1 text-xs text-color-tertiary">
                Leave blank to use the default 15-minute block.
              </p>
            )}
          </Control>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default BlockIpDialog;
